/**
 * Geografie und Kartenprojektion.
 *
 * Interne Welteinheit ist die Seemeile, Geschwindigkeiten sind Knoten (sm/h).
 * Das haelt die gesamte Navigationsmechanik in den Einheiten, in denen
 * Seekarten ohnehin rechnen — keine Umrechnung im Spielcode.
 *
 * Projektion ist Mercator: bei einem Seefahrtsspiel die sachlich richtige Wahl,
 * weil Kurse konstanter Peilung (Loxodrome) darin gerade Linien sind. Genau das
 * plant der Spieler auf der Karte.
 */

/** Mittlerer Erdradius in Seemeilen (6371 km / 1,852). */
export const EARTH_RADIUS_NM = 3440.0648;

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;

export interface LatLon {
  /** Breitengrad in Grad, positiv Nord. */
  readonly lat: number;
  /** Laengengrad in Grad, positiv Ost. */
  readonly lon: number;
}

/** Kartesische Weltkoordinate in Seemeilen. x nach Osten, y nach Sueden (Bildschirmkonvention). */
export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

/** Kartenausschnitt des Spiels. Umfasst das Dreieck mit etwas Rand. */
export const MAP_BOUNDS = {
  north: 34,
  south: 17,
  west: -82,
  east: -63,
} as const;

/** Die drei klassischen Eckpunkte des Bermuda-Dreiecks. */
export const TRIANGLE_VERTICES = {
  miami: { lat: 25.7617, lon: -80.1918 },
  sanJuan: { lat: 18.4655, lon: -66.1057 },
  bermuda: { lat: 32.2949, lon: -64.7814 },
} as const satisfies Record<string, LatLon>;

/** Mercator-Ordinate (dimensionslos), positiv nach Norden. */
function mercatorY(latDeg: number): number {
  // Bei +-90 Grad divergiert die Projektion; fuer den Spielausschnitt irrelevant,
  // aber ein Clamp verhindert Inf/NaN bei fehlerhaften Eingaben.
  const lat = Math.max(-85, Math.min(85, latDeg));
  return Math.log(Math.tan(Math.PI / 4 + (lat * DEG) / 2));
}

function inverseMercatorY(y: number): number {
  return (2 * Math.atan(Math.exp(y)) - Math.PI / 2) * RAD;
}

const ORIGIN_X = MAP_BOUNDS.west * DEG;
const ORIGIN_Y = mercatorY(MAP_BOUNDS.north);

/** Groesse des Kartenausschnitts in Seemeilen. */
export function worldSize(): { width: number; height: number } {
  const bottomRight = toWorld({ lat: MAP_BOUNDS.south, lon: MAP_BOUNDS.east });
  return { width: bottomRight.x, height: bottomRight.y };
}

export function toWorld(p: LatLon): WorldPoint {
  return {
    x: EARTH_RADIUS_NM * (p.lon * DEG - ORIGIN_X),
    y: EARTH_RADIUS_NM * (ORIGIN_Y - mercatorY(p.lat)),
  };
}

export function toLatLon(p: WorldPoint): LatLon {
  return {
    lat: inverseMercatorY(ORIGIN_Y - p.y / EARTH_RADIUS_NM),
    lon: (ORIGIN_X + p.x / EARTH_RADIUS_NM) * RAD,
  };
}

/**
 * Grosskreisentfernung in Seemeilen (Haversine).
 * Die tatsaechlich kuerzeste Strecke — Grundlage fuer Reisezeitschaetzungen.
 */
export function distanceNm(a: LatLon, b: LatLon): number {
  const dLat = (b.lat - a.lat) * DEG;
  const dLon = (b.lon - a.lon) * DEG;
  const lat1 = a.lat * DEG;
  const lat2 = b.lat * DEG;

  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Loxodromentfernung in Seemeilen — Strecke bei konstant gehaltener Peilung.
 * Immer mindestens so lang wie der Grosskreis, dafuer ohne Kurskorrektur zu fahren.
 */
export function rhumbDistanceNm(a: LatLon, b: LatLon): number {
  const dLat = (b.lat - a.lat) * DEG;
  let dLon = (b.lon - a.lon) * DEG;

  const dPsi = mercatorY(b.lat) - mercatorY(a.lat);
  // q ist der Streckfaktor der Projektion; bei Ost-West-Kurs geht dPsi gegen 0.
  const q = Math.abs(dPsi) > 1e-12 ? dLat / dPsi : Math.cos(a.lat * DEG);

  // Immer den kuerzeren Weg um den Globus nehmen.
  if (Math.abs(dLon) > Math.PI) {
    dLon = dLon > 0 ? dLon - 2 * Math.PI : dLon + 2 * Math.PI;
  }

  return EARTH_RADIUS_NM * Math.hypot(dLat, q * dLon);
}

/** Anfangspeilung des Grosskreises in Grad (0 = Nord, im Uhrzeigersinn). */
export function initialBearingDeg(a: LatLon, b: LatLon): number {
  const lat1 = a.lat * DEG;
  const lat2 = b.lat * DEG;
  const dLon = (b.lon - a.lon) * DEG;

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  return (Math.atan2(y, x) * RAD + 360) % 360;
}

/** Konstante Peilung eines Loxodroms in Grad — der Kurs, den der Spieler steuert. */
export function rhumbBearingDeg(a: LatLon, b: LatLon): number {
  let dLon = (b.lon - a.lon) * DEG;
  const dPsi = mercatorY(b.lat) - mercatorY(a.lat);

  if (Math.abs(dLon) > Math.PI) {
    dLon = dLon > 0 ? dLon - 2 * Math.PI : dLon + 2 * Math.PI;
  }

  return (Math.atan2(dLon, dPsi) * RAD + 360) % 360;
}

/** Zielpunkt nach `distance` Seemeilen auf Grosskreiskurs `bearingDeg`. */
export function destinationPoint(from: LatLon, bearingDeg: number, distanceNm: number): LatLon {
  const delta = distanceNm / EARTH_RADIUS_NM;
  const theta = bearingDeg * DEG;
  const lat1 = from.lat * DEG;
  const lon1 = from.lon * DEG;

  const sinLat2 =
    Math.sin(lat1) * Math.cos(delta) + Math.cos(lat1) * Math.sin(delta) * Math.cos(theta);
  const lat2 = Math.asin(Math.min(1, Math.max(-1, sinLat2)));

  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(theta) * Math.sin(delta) * Math.cos(lat1),
      Math.cos(delta) - Math.sin(lat1) * sinLat2,
    );

  return {
    lat: lat2 * RAD,
    // Auf [-180, 180) normalisieren.
    lon: (((lon2 * RAD + 540) % 360) + 360) % 360 - 180,
  };
}

/** Punkt entlang der Strecke a→b, `fraction` in [0, 1]. */
export function interpolate(a: LatLon, b: LatLon, fraction: number): LatLon {
  const total = distanceNm(a, b);
  if (total < 1e-9) return a;
  return destinationPoint(a, initialBearingDeg(a, b), total * fraction);
}

function sign(p: WorldPoint, a: WorldPoint, b: WorldPoint): number {
  return (p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y);
}

/**
 * Liegt der Punkt innerhalb des Bermuda-Dreiecks?
 * Getestet wird in projizierten Koordinaten — das entspricht dem Dreieck,
 * wie es der Spieler auf der Karte sieht.
 */
export function isInsideTriangle(p: LatLon): boolean {
  const pt = toWorld(p);
  const a = toWorld(TRIANGLE_VERTICES.miami);
  const b = toWorld(TRIANGLE_VERTICES.sanJuan);
  const c = toWorld(TRIANGLE_VERTICES.bermuda);

  const d1 = sign(pt, a, b);
  const d2 = sign(pt, b, c);
  const d3 = sign(pt, c, a);

  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;

  // Nur wenn alle Vorzeichen gleich sind, liegt der Punkt innen.
  return !(hasNeg && hasPos);
}

/** Liegt der Punkt im dargestellten Kartenausschnitt? */
export function isInsideMap(p: LatLon): boolean {
  return (
    p.lat >= MAP_BOUNDS.south &&
    p.lat <= MAP_BOUNDS.north &&
    p.lon >= MAP_BOUNDS.west &&
    p.lon <= MAP_BOUNDS.east
  );
}

/** Reisezeit in Stunden bei gegebener Fahrt. */
export function travelHours(distanceNm: number, speedKnots: number): number {
  if (speedKnots <= 0) return Number.POSITIVE_INFINITY;
  return distanceNm / speedKnots;
}

/** Peilung als Seemannssprache — fuer Funksprueche und Logbucheintraege. */
const COMPASS_POINTS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
] as const;

export function compassPoint(bearingDeg: number): string {
  const idx = Math.round((((bearingDeg % 360) + 360) % 360) / 22.5) % 16;
  return COMPASS_POINTS[idx] as string;
}

/** Koordinate im Seekartenformat, z. B. 25°45.7'N 080°11.5'W. */
export function formatPosition(p: LatLon): string {
  const fmt = (value: number, degWidth: number, pos: string, neg: string): string => {
    const hemisphere = value >= 0 ? pos : neg;
    const abs = Math.abs(value);
    const deg = Math.floor(abs);
    const min = (abs - deg) * 60;
    return `${String(deg).padStart(degWidth, '0')}°${min.toFixed(1).padStart(4, '0')}'${hemisphere}`;
  };

  return `${fmt(p.lat, 2, 'N', 'S')} ${fmt(p.lon, 3, 'E', 'W')}`;
}
